import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Volume2, RotateCcw, Star, Heart, Sparkles, Shuffle } from 'lucide-react';

interface VocabularyCard {
  id: number;
  english: string;
  french: string;
  category: 'colors' | 'numbers' | 'animals' | 'adjectives' | 'actions';
  emoji: string;
  example: string;
  difficulty: 'easy' | 'medium';
}

const vocabularyCards: VocabularyCard[] = [
  // Colors (Easy)
  { id: 1, english: "blue", french: "bleu", category: "colors", emoji: "💙", example: "The door is blue", difficulty: "easy" },
  { id: 2, english: "red", french: "rouge", category: "colors", emoji: "❤️", example: "Red roses are beautiful", difficulty: "easy" },
  { id: 3, english: "yellow", french: "jaune", category: "colors", emoji: "💛", example: "Yellow sunflowers", difficulty: "easy" },
  { id: 4, english: "green", french: "vert", category: "colors", emoji: "💚", example: "Spark is green", difficulty: "easy" },
  { id: 5, english: "purple", french: "violet", category: "colors", emoji: "💜", example: "Purple butterflies", difficulty: "easy" },
  { id: 6, english: "pink", french: "rose", category: "colors", emoji: "🩷", example: "Luna's horn is pink", difficulty: "easy" },
  { id: 7, english: "white", french: "blanc", category: "colors", emoji: "🤍", example: "Luna is white", difficulty: "easy" },
  { id: 8, english: "orange", french: "orange", category: "colors", emoji: "🧡", example: "Orange wings", difficulty: "easy" },

  // Numbers (Easy)
  { id: 9, english: "one", french: "un", category: "numbers", emoji: "1️⃣", example: "One magical door", difficulty: "easy" },
  { id: 10, english: "two", french: "deux", category: "numbers", emoji: "2️⃣", example: "Two friends", difficulty: "easy" },
  { id: 11, english: "three", french: "trois", category: "numbers", emoji: "3️⃣", example: "Three baby unicorns", difficulty: "easy" },
  { id: 12, english: "four", french: "quatre", category: "numbers", emoji: "4️⃣", example: "Four magical gems", difficulty: "easy" },
  { id: 13, english: "five", french: "cinq", category: "numbers", emoji: "5️⃣", example: "Five butterflies", difficulty: "easy" },

  // Animals (Easy)
  { id: 14, english: "unicorn", french: "licorne", category: "animals", emoji: "🦄", example: "Luna is a unicorn", difficulty: "easy" },
  { id: 15, english: "dragon", french: "dragon", category: "animals", emoji: "🐉", example: "Spark is a dragon", difficulty: "easy" },
  { id: 16, english: "butterfly", french: "papillon", category: "animals", emoji: "🦋", example: "Purple butterflies dance", difficulty: "easy" },

  // Adjectives (Easy & Medium)
  { id: 17, english: "happy", french: "heureux", category: "adjectives", emoji: "😊", example: "Betty is happy", difficulty: "easy" },
  { id: 18, english: "sad", french: "triste", category: "adjectives", emoji: "😢", example: "Spark is sad", difficulty: "easy" },
  { id: 19, english: "magical", french: "magique", category: "adjectives", emoji: "✨", example: "A magical door", difficulty: "medium" },
  { id: 20, english: "beautiful", french: "beau", category: "adjectives", emoji: "🌟", example: "A beautiful garden", difficulty: "medium" },
  { id: 21, english: "friendly", french: "amical", category: "adjectives", emoji: "🤗", example: "Spark is friendly", difficulty: "medium" },
  { id: 22, english: "special", french: "spécial", category: "adjectives", emoji: "⭐", example: "Betty is special", difficulty: "medium" },

  // Actions (Easy & Medium)
  { id: 23, english: "open", french: "ouvrir", category: "actions", emoji: "🚪", example: "Betty opens the door", difficulty: "easy" },
  { id: 24, english: "find", french: "trouver", category: "actions", emoji: "🔍", example: "They find the gems", difficulty: "easy" },
  { id: 25, english: "dance", french: "danser", category: "actions", emoji: "💃", example: "Butterflies dance", difficulty: "easy" },
  { id: 26, english: "work", french: "travailler", category: "actions", emoji: "🤝", example: "They work together", difficulty: "medium" },
  { id: 27, english: "help", french: "aider", category: "actions", emoji: "🤲", example: "Betty helps Spark", difficulty: "medium" },
  { id: 28, english: "sparkle", french: "briller", category: "actions", emoji: "✨", example: "The door sparkles", difficulty: "medium" }
];

export const VocabularyFlashcards: React.FC = () => {
  const [currentCard, setCurrentCard] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [studyMode, setStudyMode] = useState<'all' | 'easy' | 'medium'>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [masteredCards, setMasteredCards] = useState<number[]>([]);
  const [shuffledCards, setShuffledCards] = useState(vocabularyCards);

  const filteredCards = shuffledCards.filter(card => {
    const difficultyMatch = studyMode === 'all' || card.difficulty === studyMode;
    const categoryMatch = categoryFilter === 'all' || card.category === categoryFilter;
    return difficultyMatch && categoryMatch;
  });

  const currentCardData = filteredCards[currentCard] || vocabularyCards[0];

  const playAudio = (text: string, isEnglish: boolean) => {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = isEnglish ? 'en-US' : 'fr-FR';
      utterance.rate = 0.7;
      utterance.pitch = 1.1;
      
      const voices = speechSynthesis.getVoices();
      const femaleVoice = voices.find(voice => 
        voice.lang.startsWith(isEnglish ? 'en' : 'fr') && 
        (voice.name.toLowerCase().includes('female') || 
         voice.name.toLowerCase().includes('woman') ||
         voice.name.toLowerCase().includes('samantha'))
      );
      
      if (femaleVoice) {
        utterance.voice = femaleVoice;
      }
      
      speechSynthesis.speak(utterance);
    }
  };

  const nextCard = () => {
    if (currentCard < filteredCards.length - 1) {
      setCurrentCard(currentCard + 1);
      setIsFlipped(false);
    } else {
      setCurrentCard(0);
      setIsFlipped(false);
    }
  };

  const prevCard = () => {
    if (currentCard > 0) {
      setCurrentCard(currentCard - 1);
      setIsFlipped(false);
    } else {
      setCurrentCard(filteredCards.length - 1);
      setIsFlipped(false);
    }
  };

  const shuffleCards = () => {
    const shuffled = [...vocabularyCards].sort(() => Math.random() - 0.5);
    setShuffledCards(shuffled);
    setCurrentCard(0);
    setIsFlipped(false);
  };

  const markAsMastered = () => {
    if (!masteredCards.includes(currentCardData.id)) {
      setMasteredCards([...masteredCards, currentCardData.id]);
    }
    nextCard();
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'colors': return 'bg-rainbow-100 text-rainbow-700';
      case 'numbers': return 'bg-blue-100 text-blue-700';
      case 'animals': return 'bg-green-100 text-green-700';
      case 'adjectives': return 'bg-purple-100 text-purple-700';
      case 'actions': return 'bg-orange-100 text-orange-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const categories = ['all', 'colors', 'numbers', 'animals', 'adjectives', 'actions'];

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Controls */}
      <Card className="bg-gradient-to-r from-purple-50 to-pink-50">
        <CardHeader>
          <CardTitle className="text-2xl text-center text-purple-600 flex items-center justify-center gap-2">
            <Sparkles className="w-6 h-6" />
            Betty's Magic Word Cards
            <Sparkles className="w-6 h-6" />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4 mb-4">
            {/* Study Mode */}
            <div>
              <label className="text-sm font-medium text-purple-700 mb-2 block">📚 Study Level:</label>
              <div className="flex gap-1">
                {['all', 'easy', 'medium'].map(mode => (
                  <Button
                    key={mode}
                    variant={studyMode === mode ? "default" : "outline"}
                    size="sm"
                    onClick={() => {
                      setStudyMode(mode as any);
                      setCurrentCard(0);
                      setIsFlipped(false);
                    }}
                    className="capitalize"
                  >
                    {mode}
                  </Button>
                ))}
              </div>
            </div>

            {/* Category Filter */}
            <div>
              <label className="text-sm font-medium text-pink-700 mb-2 block">🎯 Category:</label>
              <select
                value={categoryFilter}
                onChange={(e) => {
                  setCategoryFilter(e.target.value);
                  setCurrentCard(0);
                  setIsFlipped(false);
                }}
                className="w-full p-2 border-2 border-pink-200 rounded focus:border-pink-400 focus:outline-none"
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>
                    {cat === 'all' ? 'All Categories' : cat.charAt(0).toUpperCase() + cat.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            {/* Actions */}
            <div>
              <label className="text-sm font-medium text-blue-700 mb-2 block">🎮 Actions:</label>
              <div className="flex gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={shuffleCards}
                  className="bg-blue-50 hover:bg-blue-100"
                >
                  <Shuffle className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setCurrentCard(0);
                    setIsFlipped(false);
                  }}
                  className="bg-green-50 hover:bg-green-100"
                >
                  <RotateCcw className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          <div className="text-center text-sm text-gray-600">
            Card {currentCard + 1} of {filteredCards.length} • 
            Mastered: {masteredCards.length}/{vocabularyCards.length}
          </div>
        </CardContent>
      </Card>

      {/* Flashcard */}
      <div className="flex justify-center">
        <div 
          className="relative w-80 h-96 cursor-pointer"
          onClick={() => setIsFlipped(!isFlipped)}
        >
          <div className={`absolute inset-0 w-full h-full transition-transform duration-500 transform-style-preserve-3d ${isFlipped ? 'rotate-y-180' : ''}`}>
            {/* Front of card (English) */}
            <Card className="absolute inset-0 w-full h-full backface-hidden bg-gradient-to-br from-blue-50 to-purple-50 border-4 border-blue-200">
              <CardContent className="flex flex-col items-center justify-center h-full text-center p-6">
                <div className="text-6xl mb-4">{currentCardData.emoji}</div>
                <Badge className={`mb-3 ${getCategoryColor(currentCardData.category)}`}>
                  {currentCardData.category.toUpperCase()}
                </Badge>
                <h2 className="text-3xl font-bold text-blue-700 mb-2">
                  {currentCardData.english}
                </h2>
                <p className="text-sm text-gray-600 mb-4">
                  {currentCardData.example}
                </p>
                <Button
                  variant="outline"
                  onClick={(e) => {
                    e.stopPropagation();
                    playAudio(currentCardData.english, true);
                  }}
                  className="bg-white/80"
                >
                  <Volume2 className="w-4 h-4 mr-1" />
                  🇬🇧 Listen
                </Button>
                <p className="text-xs text-gray-500 mt-4">Click to flip!</p>
              </CardContent>
            </Card>

            {/* Back of card (French) */}
            <Card className="absolute inset-0 w-full h-full backface-hidden rotate-y-180 bg-gradient-to-br from-pink-50 to-red-50 border-4 border-pink-200">
              <CardContent className="flex flex-col items-center justify-center h-full text-center p-6">
                <div className="text-6xl mb-4">{currentCardData.emoji}</div>
                <Badge className={`mb-3 ${getCategoryColor(currentCardData.category)}`}>
                  {currentCardData.category.toUpperCase()}
                </Badge>
                <h2 className="text-3xl font-bold text-pink-700 mb-2">
                  {currentCardData.french}
                </h2>
                <p className="text-sm text-gray-600 mb-4 italic">
                  "{currentCardData.english}" in French
                </p>
                <Button
                  variant="outline"
                  onClick={(e) => {
                    e.stopPropagation();
                    playAudio(currentCardData.french, false);
                  }}
                  className="bg-white/80"
                >
                  <Volume2 className="w-4 h-4 mr-1" />
                  🇫🇷 Écouter
                </Button>
                <p className="text-xs text-gray-500 mt-4">Click to flip back!</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Navigation & Actions */}
      <Card>
        <CardContent className="p-4">
          <div className="flex justify-between items-center">
            <Button
              onClick={prevCard}
              variant="outline"
              className="bg-purple-50 hover:bg-purple-100"
            >
              ← Previous
            </Button>

            <div className="flex gap-2">
              <Button
                onClick={markAsMastered}
                variant="outline"
                className="bg-green-50 hover:bg-green-100 text-green-700"
                disabled={masteredCards.includes(currentCardData.id)}
              >
                {masteredCards.includes(currentCardData.id) ? (
                  <>
                    <Star className="w-4 h-4 mr-1 fill-current" />
                    Mastered!
                  </>
                ) : (
                  <>
                    <Heart className="w-4 h-4 mr-1" />
                    I Know This!
                  </>
                )}
              </Button>
            </div>

            <Button
              onClick={nextCard}
              variant="outline"
              className="bg-purple-50 hover:bg-purple-100"
            >
              Next →
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Progress */}
      <Card className="bg-gradient-to-r from-green-50 to-blue-50">
        <CardContent className="p-4">
          <div className="text-center">
            <h4 className="font-semibold text-green-700 mb-2">🌟 Your Progress</h4>
            <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
              <div 
                className="bg-green-500 h-3 rounded-full transition-all duration-300"
                style={{ width: `${(masteredCards.length / vocabularyCards.length) * 100}%` }}
              ></div>
            </div>
            <p className="text-sm text-gray-600">
              {masteredCards.length} of {vocabularyCards.length} words mastered 
              ({Math.round((masteredCards.length / vocabularyCards.length) * 100)}%)
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};